import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, computed, inject, input, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DatePipe } from '@angular/common';
import { TranslatePipe } from '@ngx-translate/core';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { TestCaseService } from '../../../services/test-case.service';
import { TestCaseActivity, TestCaseActivityType } from '../../../models/test-case.model';

/** Icon-Form je Aktivitätstyp; im Template per @switch zu Inline-SVG aufgelöst. */
type ActivityIcon = 'plus' | 'pencil' | 'link' | 'check' | 'cross' | 'comment';

/** Farb-Variante des Icon-Kreises (siehe SCSS), abgeleitet aus dem Design-Bundle. */
type ActivityTone = 'accent' | 'blue' | 'green' | 'red' | 'muted';

/** Aufbereiteter Feed-Eintrag fürs Template (Optik + i18n-Parameter vorberechnet). */
interface ActivityItem {
  readonly id: string;
  readonly type: TestCaseActivityType;
  readonly icon: ActivityIcon;
  readonly tone: ActivityTone;
  readonly createdAt: string;
  readonly createdByName: string | null;
  /** Vollständiger i18n-Key des Titels (typ-abhängig, ggf. Varianten). */
  readonly titleKey: string;
  /** Interpolations-Parameter für den i18n-Titel (reference/action). */
  readonly params: Record<string, string>;
}

const TITLE_KEY_PREFIX = 'testCases.form.activity.types.';

const ICON_BY_TYPE: Record<TestCaseActivityType, ActivityIcon> = {
  CREATED: 'plus',
  EDITED: 'pencil',
  LINKED: 'link',
  UNLINKED: 'link',
  STEP_ADDED: 'pencil',
  STEP_PASSED: 'check',
  STEP_FAILED: 'cross',
  COMMENT: 'comment',
  HANDED_OVER: 'link',
  HANDOVER_CLOSED: 'check',
  HANDOVER_REOPENED: 'pencil',
};

const TONE_BY_TYPE: Record<TestCaseActivityType, ActivityTone> = {
  CREATED: 'accent',
  EDITED: 'accent',
  LINKED: 'blue',
  UNLINKED: 'blue',
  STEP_ADDED: 'accent',
  STEP_PASSED: 'green',
  STEP_FAILED: 'red',
  COMMENT: 'muted',
  HANDED_OVER: 'blue',
  HANDOVER_CLOSED: 'green',
  HANDOVER_REOPENED: 'accent',
};

/**
 * Aktivitäts-Feed eines Testfalls als vertikale Timeline (NEX-392): farbiger
 * Icon-Kreis, Aktions-Titel, Autor und Zeitstempel. Lädt aus
 * {@code GET /test-cases/{id}/activity} (neueste zuerst). Bewusst ohne eigenen
 * Karten-Rahmen — wird in die "Details & Historie"-Karte des Formulars
 * eingebettet, analog zur abgelösten Versions-Liste.
 *
 * Design-Referenz: docs/design/v2026-06-15-u3krKZKB5ZMRJ/page-testfaelle.jsx:743-772
 * (Bundle-Hash u3krKZKB5ZMRJTqlTIt_hg).
 */
@Component({
  selector: 'app-test-case-activity-feed',
  imports: [TranslatePipe, DatePipe, MatProgressSpinner],
  templateUrl: './test-case-activity-feed.component.html',
  styleUrl: './test-case-activity-feed.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TestCaseActivityFeedComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly testCaseService = inject(TestCaseService);

  readonly projectId = input.required<string>();
  readonly testCaseId = input<string | null>(null);

  readonly loading = signal(false);
  readonly loadError = signal(false);
  private readonly activities = signal<TestCaseActivity[]>([]);
  /** true, wenn das Backend mehr Einträge hat, als der Feed anzeigt (Page.last === false). */
  readonly truncated = signal(false);

  readonly items = computed<ActivityItem[]>(() => this.activities().map((a) => this.toItem(a)));

  ngOnInit(): void {
    const id = this.testCaseId();
    if (!id) return;
    this.load(id);
  }

  private load(id: string): void {
    this.loading.set(true);
    this.loadError.set(false);
    this.testCaseService
      .listActivities(this.projectId(), id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (page) => {
          this.activities.set(page.content);
          this.truncated.set(!page.last);
          this.loading.set(false);
        },
        error: () => {
          this.loadError.set(true);
          this.loading.set(false);
        },
      });
  }

  private toItem(a: TestCaseActivity): ActivityItem {
    const params = this.buildParams(a);
    return {
      id: a.id,
      type: a.type,
      icon: ICON_BY_TYPE[a.type],
      tone: TONE_BY_TYPE[a.type],
      createdAt: a.createdAt,
      createdByName: a.createdByName ?? null,
      titleKey: this.buildTitleKey(a.type, params),
      params,
    };
  }

  /**
   * Leitet die i18n-Parameter aus dem typ-spezifischen Payload ab. Bei
   * Verknüpfungen wird das sprechende Label bevorzugt, sonst die rohe Referenz.
   * Für hinzugefügte Schritte wird der Aktionstext nur übernommen, wenn er
   * vorhanden ist (MODULE_REF-Steps haben keinen).
   */
  private buildParams(a: TestCaseActivity): Record<string, string> {
    const payload = a.payload ?? {};
    if (a.type === 'LINKED' || a.type === 'UNLINKED') {
      const reference = (payload['label'] as string) || (payload['reference'] as string) || '';
      return { reference };
    }
    if (a.type === 'STEP_ADDED') {
      const action = (payload['action'] as string) ?? '';
      return action ? { action } : {};
    }
    return {};
  }

  /**
   * Wählt den Titel-Key. Für STEP_ADDED gibt es eine Variante mit Schrittnamen,
   * sofern ein Aktionstext vorliegt. Für LINKED/UNLINKED wird ohne Referenz die
   * `_NO_REF`-Fassung gewählt, damit kein Trailing-Space-Text entsteht.
   */
  private buildTitleKey(type: TestCaseActivityType, params: Record<string, string>): string {
    if (type === 'STEP_ADDED' && params['action']) {
      return `${TITLE_KEY_PREFIX}STEP_ADDED_WITH_ACTION`;
    }
    if ((type === 'LINKED' || type === 'UNLINKED') && !params['reference']) {
      return `${TITLE_KEY_PREFIX}${type}_NO_REF`;
    }
    return `${TITLE_KEY_PREFIX}${type}`;
  }
}
